import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AggregateAlertsBy,
  AggregatedAlertDetails,
  SortOrder as NotificationSortOrder,
} from '@ariksa/notification';
import {
  AlertsApiGetAggregatedAlertsRequest,
  NotificationFor,
  Severity,
} from '@ariksa/notification/api';
import {
  Box,
  Center,
  HStack,
  ListItem,
  OrderedList,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  forEach,
  includes,
  isEmpty,
  isEqual,
  map,
  slice,
  split,
  toArray,
  toUpper,
} from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';
import { INIT_PAGE_INFO } from 'services/utils/constants';
import { Optional } from 'types/utils';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import {
  Sorted,
  SortOrder,
  TableColumnProps,
} from 'components/DataDisplay/NewTable/types';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { OutlinedIconButton } from 'components/DataEntry/Button/OutlinedIconButton';
import {
  ActionButton,
  AddExceptionActionButton,
  ChartMixedIcon,
  getIcon,
  IssuesIcon,
  LinkIcon,
  PageHeaderWithIcon,
  PlusCountTag,
  renderTableHeaderWithLoader,
  Tag,
  TicketIcon,
  WithResourceIcon,
} from 'components/index';
import { renderVulnerabilities } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { ResourceTypeIconTooltip } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import {
  selectActiveCloudAccount,
  selectActiveEnvironment,
  selectApp,
} from 'containers/App/selectors';
import { FindingDrawer } from 'containers/Findings/AlertDrawer/Components/Aggregated/FindingDrawer';
import { CreateTicket } from 'containers/Findings/Alerts/Components/CreateTicket';
import { AggregateAlertSummary } from 'containers/Findings/FindingsOverview/Components/AggregateAlertSummary';
import { FindingTableFilter } from 'containers/Findings/FindingsOverview/Components/AlertTables/FindingTableFilter';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';
import { actions } from 'containers/Findings/FindingsOverview/slice';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { useSearchParams } from 'hooks/useSearchParams';
import { toTitleCase } from 'utils/string';

interface Props {
  title: string;
  onOpenLearnMore();
}

export const FindingsTable: FC<Props> = memo(props => {
  const { title, onOpenLearnMore } = props;
  const { findings, currentCategory, findingCompliance } = useSelector(
    selectFindingsOverview,
  );

  const dispatch = useDispatch();
  const params = useSearchParams<{
    sortField: string;
    sortOrder: SortOrder;
    group_id: string;
  }>();

  const { updateValue } = useSearchParamFunctions();

  const { accountId } = useSelector(selectActiveCloudAccount);
  const { aiParamExtraction, blueprints } = useSelector(selectSharedState);
  const { environmentId } = useSelector(selectActiveEnvironment);
  const { user } = useSelector(selectApp);

  const findingDrawer = useDisclosure();
  const summaryDrawer = useDisclosure();
  const ticketModal = useDisclosure();
  const { accountMapping } = useCloudAccountId();
  const navigate = useNavigate();
  const location = useLocation();

  const blueprintMapping = useMemo(
    () =>
      new Map((blueprints.data ?? []).map(item => [item.id, item] as const)),
    [blueprints.data],
  );

  // Alert Vault returns immutable blueprint IDs from the alert facts. The
  // shared-state blueprint list supplies the user-facing names and keeps this
  // aggregate API free of a per-row PolicyHub lookup.
  const tableData = useMemo(
    () =>
      findings.data.map(rawRow => {
        const row = rawRow as AggregatedAlertDetails & {
          blueprint_ids?: string[];
        };
        const accountIds = row.account_id_list?.length
          ? row.account_id_list
          : row.account_id
          ? [row.account_id]
          : [];
        const existingBlueprintIds =
          row.blueprints?.map(item => item.blueprint_id) ?? [];
        const blueprintIds = row.blueprint_ids?.length
          ? row.blueprint_ids
          : existingBlueprintIds.length
          ? existingBlueprintIds
          : (row.alert_rule_ids ?? []).filter(id => blueprintMapping.has(id));
        const resolvedBlueprints = row.blueprints?.length
          ? row.blueprints
          : blueprintIds.map(id => {
              const blueprint = blueprintMapping.get(id);
              return {
                blueprint_id: id,
                blueprint_name: blueprint?.name ?? id,
              };
            });

        return {
          ...row,
          account_id_list: accountIds,
          blueprints: resolvedBlueprints,
        };
      }),
    [blueprintMapping, findings.data],
  );

  const [finding, setFinding] = useState<Optional<AggregatedAlertDetails>>(
    undefined,
  );
  const [copied, setCopied] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const [tabIndex, setTabIndex] = useState(0);
  const [sortByField, setSortByField] = useState<Sorted>({} as Sorted);

  useEffect(() => {
    if (!!params?.sortOrder && !!params?.sortField)
      setSortByField({
        sortOrder: params?.sortOrder as SortOrder,
        sortField: params?.sortField,
      });
  }, [params?.sortOrder, params?.sortField]);

  const getFindings = useCallback(
    (pageInfo = INIT_PAGE_INFO) => {
      let payload: AlertsApiGetAggregatedAlertsRequest = {
        environmentId: environmentId,
        accountId,
        pageNumber: pageInfo.page_number,
        pageSize: pageInfo.page_size,
        categoryClass: !!params?.categoryClass
          ? params?.categoryClass
          : undefined,
        aggregateBy: AggregateAlertsBy.GroupId,
        alertCategory:
          currentCategory === 'AllFindings'
            ? undefined
            : (currentCategory as string),
        severities: !!params?.severity
          ? (split(params?.severity, ',') as Severity[])
          : undefined,
        resourceTypes: !!params?.resourceType
          ? (split(params?.resourceType, ',') as Severity[])
          : undefined,
        sortField: !!sortByField?.sortField
          ? sortByField?.sortField
          : undefined,
        sortOrder: !!sortByField?.sortOrder
          ? (toUpper(sortByField?.sortOrder) as NotificationSortOrder)
          : undefined,
        groupIds: !!params.group_id ? [params.group_id] : undefined,
        search: aiParamExtraction?.data?.search,
      };
      dispatch(
        actions.getFindings({
          q: payload,
          page: pageInfo,
          onSuccess: res => {
            const ruleIds = new Set();
            forEach(res?.aggregated_alerts, o => {
              forEach(o.alert_rule_ids, id => ruleIds.add(id));
            });
            !isEmpty(ruleIds) &&
              dispatch(
                actions.getComplianceForFinding({
                  q: {
                    getRuleComplianceStandardRequest: {
                      rules: toArray(ruleIds) as string[],
                    },
                  },
                }),
              );
          },
        }),
      );
    },
    [
      dispatch,
      environmentId,
      accountId,
      currentCategory,
      sortByField,
      params.group_id,
      params?.resourceType,
      params?.severity,
      params?.categoryClass,
      location,
      aiParamExtraction?.data?.search,
    ],
  );

  useEffect(() => {
    getFindings();
  }, [getFindings]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCopied(false);
    }, 8000);

    // Cleanup to prevent memory leaks
    return () => clearTimeout(timer);
  }, []);

  const renderCompliance = ({ row }) => {
    let compliance = new Set();
    forEach(row?.alert_rule_ids, o => {
      forEach(findingCompliance.data?.[o], c => compliance.add(c));
    });
    const items = toArray(compliance) as string[];
    const paddingTopArray = ['NIST-800-53 Rev 5', 'CIS_AWS_1.4', 'CIS_AWS_1.5'];
    const renderItem = items => (
      <HStack>
        {map(items, o => (
          <CustomTooltip label={o}>
            <Box boxSize={5}>
              <Center
                pt={
                  includes(paddingTopArray, o)
                    ? 1.5
                    : o === 'PCI-DSS_v3.2.1' || o === 'PCI DSS 4.0.1'
                    ? 0.5
                    : 0
                }
              >
                {getIcon(o)}
              </Center>
            </Box>
          </CustomTooltip>
        ))}
      </HStack>
    );
    return (
      <Stack>
        {renderItem(slice(items, 0, 3))}
        <HStack>
          {renderItem(slice(items, 3, 5))}
          {items.length > 5 && (
            <CustomTooltip
              label={
                <Stack>
                  {map(slice(items, 5, items.length), o => (
                    <Box>{o}</Box>
                  ))}
                </Stack>
              }
            >
              <PlusCountTag count={items.length - 5} />
            </CustomTooltip>
          )}
        </HStack>
      </Stack>
    );
  };

  const columns: TableColumnProps[] = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Description</Box>,
      render: ({ row }) => {
        const type = row?.multi_resource
          ? 'CloudService'
          : row?.resource_types?.[0];
        return (
          <HStack w="full" justify="space-between">
            <WithResourceIcon
              resourceType={type}
              color={row?.multi_resource ? 'primary' : undefined}
              iconTooltip={<ResourceTypeIconTooltip resourceType={type} />}
              tooltip={row.description}
            >
              <Text whiteSpace="wrap" noOfLines={2}>
                {row.description}
              </Text>
            </WithResourceIcon>
            <Box
              boxSize={5}
              color="primary"
              cursor="pointer"
              onClick={e => {
                e.stopPropagation();
                setCurrentRecord(row);
                navigator.clipboard.writeText(
                  (
                    location.origin +
                    '/findings/issue/' +
                    row?.group_id +
                    '?environmentId=' +
                    environmentId +
                    (!!accountId ? '&accountId=' + accountId : '') +
                    '&organization=' +
                    user.info.org_id +
                    '&aggregated=true'
                  ).toString(),
                );
                setCopied(true);
              }}
            >
              <Center>
                <CustomTooltip
                  label={
                    copied && currentRecord.group_id === row?.group_id
                      ? 'Link copied'
                      : 'Get link for issue'
                  }
                  width={20}
                  textAlign="center"
                >
                  <LinkIcon />
                </CustomTooltip>
              </Center>
            </Box>
          </HStack>
        );
      },
      align: 'left',
      styles: {
        width: '60%',
        cell: { maxW: '60%' },
      },
    },
    {
      header: 'Accounts',
      align: 'center',
      styles: {
        width: '5%',
        cell: { maxW: '5%' },
      },
      render: ({ row }) => {
        const length = row?.account_id_list?.length;
        return (
          <CustomTooltip
            label={
              <Stack spacing={1}>
                <Box color="orange">Accounts</Box>
                <OrderedList>
                  {map(row?.account_id_list, o => {
                    const account = accountMapping?.[o];
                    return (
                      <ListItem pl={3}>
                        <Text>
                          {account?.name} ({account?.cloud_account_id})
                        </Text>
                      </ListItem>
                    );
                  })}
                </OrderedList>
              </Stack>
            }
          >
            {length}
          </CustomTooltip>
        );
      },
    },
    {
      header: 'Severity',
      accessor: 'severity',
      align: 'left',
      render: ({ value }) =>
        renderVulnerabilities({
          value,
          spacing: 2,
          circle: true,
          circleSize: 5,
          showLighterBg: true,
        }),
      styles: {
        width: '10%',
        cell: { maxW: '10%' },
      },
    },
    ...(currentCategory === 'AllFindings'
      ? [
          {
            header: 'Category',
            accessor: 'alert_category',
            align: 'left',
            render: ({ value }) => toTitleCase(value),
            styles: {
              width: '120px',
              cell: { maxW: '120px' },
            },
            sortKey: 'alert_category',
          },
        ]
      : []),
    {
      header: renderTableHeaderWithLoader(
        'Frameworks',
        findingCompliance.isLoading,
      ),
      align: 'left',
      render: renderCompliance,
      styles: {
        width: '150px',
        cell: { maxW: '150px' },
      },
    },
    {
      header: 'Actions',
      align: 'center',
      styles: {
        width: '40px',
        cell: { maxW: 50 },
      },
      render: ({ row }) => {
        return (
          <Center>
            <AddExceptionActionButton
              aggregatedAlert={row}
              exceptionFor="AggregateFinding"
            />
            <ActionButton
              icon={<TicketIcon />}
              label="Create ticket"
              onClick={() => {
                ticketModal.onOpen();
                setFinding(row);
              }}
            />
          </Center>
        );
      },
    },
  ];

  const menuItems = [
    {
      key: 'alerts',
      label: 'Go To Issues',
      icon: <IssuesIcon />,
      onClick: () => {
        let category = '';
        if (
          currentCategory !== 'Overview' &&
          currentCategory !== 'AllFindings'
        ) {
          category = '?alertCategory=' + currentCategory + '&redirect=true';
          updateValue(
            'alertCategory',
            currentCategory as string,
            'findings_issues_alert_categories',
          );
        }
        navigate('/findings/alerts' + category);
      },
    },
    {
      key: 'charts',
      label: 'Show Summary',
      icon: <ChartMixedIcon />,
      onClick: () => summaryDrawer.onOpen(),
      iconSize: 6,
    },
  ];

  return (
    <ActiveResourceProvider>
      <Stack h="full" spacing={4}>
        <HStack justify="space-between" w="full">
          <Box>
            <PageHeaderWithIcon
              label={title}
              iconType={currentCategory as string}
              iconBgColor="primary"
              iconColor="white"
              reversed
            />
          </Box>
          <HStack spacing={1}>
            <LearnMoreButton onOpen={onOpenLearnMore} label="Findings" />
            {map(menuItems, (o, index) => (
              <OutlinedIconButton
                onClick={o.onClick}
                key={index + '-' + currentCategory + '-findings-overview'}
                leftIcon={o.icon}
                iconSize={o?.iconSize}
              >
                {o.label}
              </OutlinedIconButton>
            ))}
          </HStack>
        </HStack>

        <BorderedBox h="full">
          <Stack spacing={5} h="full">
            <HStack>
              <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
              <FindingTableFilter />
              {!!params?.categoryClass && (
                <Tag
                  label={params?.categoryClass}
                  closeButton
                  onClose={() =>
                    updateValue(
                      'categoryClass',
                      '',
                      'findings_overview_category_class',
                    )
                  }
                />
              )}
            </HStack>

            <Box flex={1} h="full">
              <Table
                className="dashboard_aggregated_alerts_table"
                data={tableData}
                columns={columns}
                pagination={{
                  pageInfo: findings.page.info,
                  totalCount: findings.page.totalCount,
                  cursorBased: isAlertVaultEnabled(),
                  // Alert Vault aggregates return the exact finding total.
                  exactTotal: isAlertVaultEnabled(),
                  onChange: getFindings,
                }}
                isLoading={findings.isLoading}
                styles={{ height: '32.8rem', header: { zIndex: 800 } }}
                headerClassName="dashboard_alerts_table"
                onRowClick={r => {
                  setFinding(r);
                  findingDrawer.onOpen();
                }}
                isRowActive={r => r.alert_rule_id === finding?.alert_category}
                cursor={'pointer'}
                sortBy={sortByField}
                onSort={sortInfo => {
                  if (!isEqual(sortByField, sortInfo)) {
                    setSortByField(sortInfo);
                  }
                }}
              />
            </Box>
          </Stack>
        </BorderedBox>

        {findingDrawer.isOpen && finding && (
          <FindingDrawer
            finding={finding}
            isOpen={findingDrawer.isOpen}
            onClose={findingDrawer.onClose}
            tabIndex={tabIndex}
            setTabIndex={setTabIndex}
          />
        )}

        {summaryDrawer.isOpen && (
          <AggregateAlertSummary title={title} {...summaryDrawer} />
        )}

        {ticketModal.isOpen && (
          <CreateTicket
            alert={finding ?? {}}
            notification_for={NotificationFor.GroupAlerts}
            onClose={ticketModal.onClose}
            isOpen={ticketModal.isOpen}
          />
        )}
      </Stack>
    </ActiveResourceProvider>
  );
});
